/**
 * Requester groups — Step 69, the requester half of catalogue visibility.
 *
 * A group is a set of the people who RAISE tickets, and it exists so an item
 * can be restricted to it. Deliberately not the agent groups screen: agent
 * groups are assignment queues with a members list somebody curates, and
 * these are audience definitions that are usually a RULE — "everyone in
 * Finance" is a sentence about the directory, not a list to maintain by hand.
 *
 * ── Both, because every real group turns out to be both ────────────────
 *
 * The rule and the hand-added list are additive, and that is not a hedge. Ask
 * anyone for "Finance" and the answer is "Finance, plus Dave who sits with
 * them and plus the two contractors" — a screen that offers only a rule sends
 * them to a spreadsheet, and one that offers only a list means somebody has
 * to remember this page every time a person joins.
 *
 * ── Why the preview runs on the server ─────────────────────────────────
 *
 * "Admits 40 people" computed in the browser would be a second implementation
 * of the rule, and the moment it disagrees with the catalogue nobody can tell
 * which is lying. `previewRequesterGroup` runs the evaluator that actually
 * gates the catalogue, over the same fact vocabulary. The number on screen is
 * the number the catalogue will use.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleUserRound, Plus, Trash2, UserPlus, Users, X } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { ConditionBuilder, type ConditionField } from '../../components/ConditionBuilder';
import { catalogueApi, type RequesterGroup } from '../../features/fields/api';
import { qk } from '../../lib/queryClient';
import { useConfirm } from '../../lib/confirm';
import { useDirty } from '../../lib/dirty';
import type { Condition } from '@itsm/shared';

/**
 * Exactly the attributes `directoryFacts` exposes on the server, by the same
 * keys. A field offered here that the evaluator cannot see would build a rule
 * that silently matches nobody — the same trap the automation builder's field
 * list documents.
 */
const RULE_FIELDS: ConditionField[] = [
  { key: 'department', label: 'Department' },
  { key: 'jobTitle', label: 'Job title' },
  { key: 'location', label: 'Office location' },
  { key: 'manager', label: 'Manager' },
  { key: 'company', label: 'Company' },
  { key: 'employeeType', label: 'Employee type' },
];

interface Person {
  id: string;
  name: string | null;
  email: string;
}

interface Draft {
  name: string;
  rule: Condition | undefined;
  members: Person[];
}

export function RequesterGroupsPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [newName, setNewName] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const groups = useQuery({ queryKey: qk.requesterGroups, queryFn: catalogueApi.requesterGroups });
  // The people already in a group, so the editor can show names rather than
  // the UUIDs the API stores.
  const people = useQuery({
    queryKey: qk.requesterDirectory,
    queryFn: () => apiFetch<{ entries: Person[] }>('/api/org/people?kind=requester'),
  });
  const attributes = useQuery({
    queryKey: qk.requesterAttributes,
    queryFn: catalogueApi.requesterAttributes,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.requesterGroups });

  /**
   * Dirty means CHANGED, not "open".
   *
   * `useDirty(!!draft)` would be one character shorter and wrong: opening a
   * group and closing it again would raise the unsaved-work prompt over an
   * edit nobody made. Kai hit exactly that on the review tab
   * (*"Clicking on and off the review tab triggers an unsaved changes"*), so
   * the comparison is against the loaded group, and it is the same value the
   * Save button is guarded on.
   */
  const original = groups.data?.entries.find((g) => g.id === editingId) ?? null;
  const dirty =
    !!draft &&
    !!original &&
    (draft.name !== original.name ||
      JSON.stringify(draft.rule ?? null) !== JSON.stringify(original.rule ?? null) ||
      draft.members
        .map((m) => m.id)
        .sort()
        .join(',') !== [...original.memberIds].sort().join(','));
  useDirty(dirty);

  const valueOptions = useMemo(
    () => (fieldKey: string) => (attributes.data?.[fieldKey] ?? []).map((v) => ({ value: v, label: v })),
    [attributes.data],
  );

  function openEditor(g: RequesterGroup) {
    const byId = new Map((people.data?.entries ?? []).map((p) => [p.id, p]));
    setEditingId(g.id);
    setActionError(null);
    setDraft({
      name: g.name,
      rule: g.rule ?? undefined,
      members: g.memberIds
        .map((id) => byId.get(id) ?? { id, name: null, email: 'Unknown person' })
        .sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email)),
    });
  }

  async function save() {
    if (!draft || !editingId || saving) return;
    setSaving(true);
    const res = await catalogueApi.updateRequesterGroup(editingId, {
      name: draft.name.trim(),
      // `null` is what turns a rule-based group back into a hand-picked one,
      // so an empty builder must send null rather than omit the key.
      rule: draft.rule ?? null,
      memberIds: draft.members.map((m) => m.id),
    });
    setSaving(false);
    if (!res.ok) {
      setActionError(res.error ?? 'Saving failed');
      return;
    }
    setDraft(null);
    setEditingId(null);
    invalidate();
  }

  const create = useMutation({
    mutationFn: (name: string) => catalogueApi.createRequesterGroup({ name }),
    onSuccess: (res) => {
      setActionError(res.ok ? null : res.error);
      if (res.ok) invalidate();
    },
  });

  if (groups.isPending)
    return (
      <div className="max-w-3xl space-y-3 p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (groups.isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load requester groups</p>
        <p className="text-xs text-destructive/80">{(groups.error as Error).message}</p>
        <button
          onClick={() => groups.refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="max-w-3xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <Users className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Requester groups</h1>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Audiences for the service catalogue. Restrict an item to a group and everyone else stops seeing it —
        and stops being able to request it. Membership can be a rule over directory details, a hand-picked
        list, or both.
      </p>

      <div className="mb-4 flex gap-2">
        <Input
          placeholder="New group name (e.g. Finance, Managers, Contractors)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newName.trim()) {
              create.mutate(newName.trim());
              setNewName('');
            }
          }}
        />
        <Button
          disabled={!newName.trim() || create.isPending}
          onClick={() => {
            create.mutate(newName.trim());
            setNewName('');
          }}
        >
          <Plus /> Create
        </Button>
      </div>
      {actionError && <p className="mb-3 text-xs text-destructive">{actionError}</p>}

      <div className="space-y-3">
        {groups.data!.entries.map((g) =>
          editingId === g.id && draft ? (
            <GroupEditor
              key={g.id}
              draft={draft}
              setDraft={setDraft}
              fields={RULE_FIELDS}
              valueOptions={valueOptions}
              directory={people.data?.entries ?? []}
              saving={saving}
              dirty={dirty}
              onSave={() => void save()}
              onCancel={() => {
                setDraft(null);
                setEditingId(null);
              }}
            />
          ) : (
            <div key={g.id} className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{g.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {g.rule ? 'Rule-based' : 'Hand-picked'}
                    {g.memberIds.length > 0 &&
                      ` · ${g.memberIds.length} added by hand${g.rule ? ' as well' : ''}`}
                    {!g.rule && g.memberIds.length === 0 && ' · nobody yet'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditor(g)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${g.name}`}
                    onClick={async () => {
                      if (
                        !(await confirm({
                          title: `Delete "${g.name}"?`,
                          body: 'Any catalogue item restricted to it must be changed first.',
                          confirmLabel: 'Delete',
                          destructive: true,
                        }))
                      )
                        return;
                      const res = await catalogueApi.deleteRequesterGroup(g.id);
                      setActionError(res.ok ? null : (res.error ?? 'Delete failed'));
                      if (res.ok) invalidate();
                    }}
                  >
                    <Trash2 className="text-muted-foreground" />
                  </Button>
                </div>
              </div>
            </div>
          ),
        )}
        {groups.data!.entries.length === 0 && (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No requester groups yet. Create one above, then restrict a catalogue item to it.
          </p>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   The editor — draft-first, because this is three fields at once and a
   half-applied group is an audience nobody meant to define.
   ──────────────────────────────────────────────────────────────────────── */

function GroupEditor({
  draft,
  setDraft,
  fields,
  valueOptions,
  directory,
  saving,
  dirty,
  onSave,
  onCancel,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  fields: ConditionField[];
  valueOptions: (fieldKey: string) => { value: string; label: string }[];
  directory: Person[];
  saving: boolean;
  /** Guarded on CHANGED, not merely valid — an untouched form has nothing to
   *  save, and an enabled Save on one teaches people the button means
   *  nothing. */
  dirty: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const [preview, setPreview] = useState<{ total: number; entries: Person[] } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  /**
   * URL state on purpose: this is a type-ahead inside an open draft, cleared
   * the moment somebody is picked. It is not WHERE YOU ARE — restoring it
   * from a link would drop a reader into a half-typed search over an editor
   * that is not even open, which is the transient state the plan says never
   * reaches the URL.
   */
  const [search, setSearch] = useState('');

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return directory
      .filter((p) => !draft.members.some((m) => m.id === p.id))
      .filter((p) => `${p.name ?? ''} ${p.email}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, directory, draft.members]);

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3">
        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Group name</label>
        <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      </div>

      <div className="mb-4">
        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
          Membership rule (optional)
        </label>
        <ConditionBuilder
          value={draft.rule}
          onChange={(rule) => {
            setDraft({ ...draft, rule });
            setPreview(null);
          }}
          fields={fields}
          valueOptions={valueOptions}
          defaultFieldKey="department"
          addLabel="Add a condition"
        />
        <div className="mt-2 flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={!draft.rule || previewing}
            onClick={async () => {
              if (!draft.rule) return;
              setPreviewing(true);
              const res = await catalogueApi.previewRequesterGroup(draft.rule);
              setPreviewing(false);
              setPreview(res.ok && res.body ? res.body : { total: 0, entries: [] });
            }}
          >
            <Users /> Who does this match?
          </Button>
          {preview && (
            <span className="text-xs text-muted-foreground">
              {preview.total === 0
                ? 'Nobody in the directory matches this rule.'
                : `${preview.total} ${preview.total === 1 ? 'person' : 'people'}`}
            </span>
          )}
        </div>
        {preview && preview.entries.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {preview.entries.map((p) => (
              <Badge key={p.id} variant="outline" className="font-normal">
                {p.name ?? p.email}
              </Badge>
            ))}
            {preview.total > preview.entries.length && (
              <span className="self-center text-xs text-muted-foreground">
                and {preview.total - preview.entries.length} more
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
          Added by hand {draft.rule ? '(as well as the rule)' : ''}
        </label>
        {draft.members.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {draft.members.map((m) => (
              <span
                key={m.id}
                className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-1 text-xs"
              >
                <CircleUserRound className="h-3.5 w-3.5 text-muted-foreground" />
                {m.name ?? m.email}
                <button
                  onClick={() => setDraft({ ...draft, members: draft.members.filter((x) => x.id !== m.id) })}
                  aria-label={`Remove ${m.name ?? m.email}`}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <Input
          placeholder="Search requesters by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {candidates.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {candidates.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  setDraft({ ...draft, members: [...draft.members, p] });
                  setSearch('');
                }}
                className="flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="truncate">
                  {p.name ? `${p.name} · ` : ''}
                  {p.email}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!draft.name.trim() || !dirty || saving} onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
